import { useCallback, useState } from 'react';

/**
 * La clave de idempotencia de UN intento de emisión (KAN-145). **El detalle
 * que más importa de toda la pantalla**, según la propia tarjeta: sin esto,
 * un timeout de red seguido de un reintento factura dos veces.
 *
 * # LA REGLA, EXACTA
 *
 * Se genera **una vez** al montar el formulario (inicializador perezoso de
 * `useState`: corre una sola vez, no en cada render) y sobrevive mientras el
 * intento sigue "abierto". Deja de servir —y hay que pedir una nueva con
 * {@link Clave.renovar}— en cualquiera de estos casos, todos tomados del
 * contrato (`docs/openapi.yaml`, sección de la emisión):
 *
 * - **Terminó bien** (`APROBADO`): el comprobante ya existe fiscalmente: el
 *   intento concluyó y una emisión *más* es, por definición, otro intento.
 * - **ARCA rechazó** (`RECHAZADO`): el backend **libera la clave** él mismo
 *   ("no se consumió número, no se registró nada"), así que reusarla no
 *   rompería nada — pero conceptualmente, corregir el pedido y reintentar es
 *   otro intento, y el contrato lo dice así con todas las letras.
 * - **`400` o `500`**: el contrato avisa que estos dejan la clave `EN_CURSO`
 *   **para siempre** ("nadie la libera salvo el rechazo de ARCA"). Insistir
 *   con la misma clave da `409 emision_en_curso` de por vida; hace falta una
 *   nueva sí o sí.
 * - **`422 clave_idempotencia_reusada`**: sería un bug del cliente (dos
 *   pedidos distintos con la misma clave). No debería pasar si esta regla se
 *   sigue, pero si pasara, seguir insistiendo con la misma clave repetiría el
 *   mismo error.
 *
 * # LO QUE SÍ MANTIENE LA MISMA CLAVE
 *
 * - **Sin respuesta** (`sin_conexion`): no se sabe si el POST llegó a
 *   reservar la clave. Reintentar con una clave nueva sería exactamente el
 *   doble-cobro que la idempotencia existe para evitar.
 * - **`409 emision_en_curso`**: otra request con esa misma clave está en
 *   vuelo. El contrato pide, textual, reintentar "en unos segundos con la
 *   MISMA clave".
 *
 * `renovar()` no se llama sola: quien maneja la mutación decide, mirando el
 * resultado, si corresponde. Ver `FormularioDeEmision`.
 */
export interface Clave {
  /** El valor a mandar en el header `Idempotency-Key` de este intento. */
  readonly clave: string;
  /** Empieza un intento nuevo: la próxima emisión va con una clave distinta. */
  readonly renovar: () => void;
}

export function useClaveDeIdempotencia(): Clave {
  const [clave, setClave] = useState<string>(() => crypto.randomUUID());
  const renovar = useCallback(() => setClave(crypto.randomUUID()), []);
  return { clave, renovar };
}
